/**
 * Shared reader for the Phase 0 scenario fixtures.
 *
 * Both harnesses use it — the jsdom parity test (which reads the file with
 * `fs`) and the browser parity page (which imports the JSON directly) — so the
 * two can never disagree about what a scenario means.
 *
 * Source of truth:
 * `.specify/features/036-master-template-format/fixtures/scenarios.json`.
 */

import type { PublicEvent, PublicGiftItem } from "@/services/api/publicEvents";

export type ScenarioGifts = {
  cardPaymentsEnabled: boolean;
  stripeAccountId: string | null;
  categories: Array<{ id: string | null; name: string }>;
  gifts: PublicGiftItem[];
};

export type ScenarioEnv = {
  locale: string;
  now: string;
  prefersReducedMotion: boolean;
  viewports: number[];
  draftPreview: boolean;
};

export type Scenario = {
  id: string;
  proves: string;
  expect?: Record<string, unknown>;
  event: PublicEvent & { hasEnabledGifts: boolean };
  gifts: ScenarioGifts;
  env: ScenarioEnv;
};

export type ScenarioFile = {
  base: { event: unknown; gifts: unknown; env: unknown };
  scenarios: Array<{ id: string; proves: string; expect?: Record<string, unknown>; patch: Json }>;
};

type Json = Record<string, unknown>;

/** Deep merge; a patch value of null clears the key, arrays replace wholesale. */
function merge(base: Json, patch: Json): Json {
  const out: Json = { ...base };
  for (const [key, value] of Object.entries(patch)) {
    const current = out[key];
    const bothPlainObjects =
      value !== null && typeof value === "object" && !Array.isArray(value) &&
      current !== null && typeof current === "object" && !Array.isArray(current);
    out[key] = bothPlainObjects ? merge(current as Json, value as Json) : value;
  }
  return out;
}

export function buildScenarios(file: ScenarioFile): Scenario[] {
  return file.scenarios.map((entry) => {
    const merged = merge(file.base as unknown as Json, entry.patch) as unknown as {
      event: PublicEvent;
      gifts: ScenarioGifts;
      env: ScenarioEnv;
    };

    // What EventPublicPage computes from the gifts endpoint and hands to the
    // templates; `shouldShowGiftRegistry` reads it.
    const hasEnabledGifts =
      Boolean(merged.event.registryPublic) &&
      Boolean(merged.event.giftsActive) &&
      merged.gifts.gifts.length > 0;

    return {
      id: entry.id,
      proves: entry.proves,
      expect: entry.expect,
      event: { ...merged.event, hasEnabledGifts },
      gifts: merged.gifts,
      env: merged.env,
    };
  });
}
